import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Button,
  Form,
} from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Video } from '../../types/Video';
import { dataService } from '../../services/DataService';
import VideoForm from './VideoForm';
import AdminVideoPreview from '../video/AdminVideoPreview';

interface ModuleVideosProps {
  moduleId: string;
}

export default function ModuleVideos({ moduleId }: ModuleVideosProps) {
  const [videos, setVideos] = useState<Video[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadVideos = async () => {
    setIsLoading(true);
    const res = await dataService.getItems(`modules/${moduleId}/videos`);
    if (res.statusCode === 200) {
      const videosData = res.body.map((video: any) => ({
        ...video,
        id: video.id || video.docId || video.documentId,
      }));
      setVideos(videosData);
    }
    setIsLoading(false);
  };

  const handleSave = async (video: Video) => {
    try {
      if (isEditing && isEditing !== 'new') {
        // Update existing video
        await dataService.updateItem(`modules/${moduleId}/videos`, video.id, video);
      } else {
        // Create new video - let Firebase generate ID
        await dataService.setItem(`modules/${moduleId}/videos`, undefined, {
          ...video,
          moduleId,
          order: videos.length,
        });
      }
      setIsEditing(null);
      loadVideos();
    } catch (error) {
      console.error('Error saving video:', error);
    }
  };

  const handleAddEdit = (video: Video) => {
    handleSave(video);
  };

  const handleDelete = async (videoId: string) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await dataService.deleteItem(`modules/${moduleId}/videos`, videoId);
        loadVideos();
      } catch (error) {
        console.error('Error deleting video:', error);
      }
    }
  };

  const getEmptyVideo = (): Video => ({
    id: '', // Add empty string as placeholder
    moduleId,
    order: videos.length,
    videoId: '',
    videoId_fr: '',
    videoId_it: '',
    title: '',
    title_fr: '',
    title_it: '',
  });

  const reorderVideos = async (items: Video[]) => {
    try {
      const reorderData = {
        collection: `modules/${moduleId}/videos`,
        items: items.map((video, index) => ({
          id: video.id, // This is now guaranteed to be a string
          order: index,
        })),
      };

      await dataService.reorderItems(reorderData);
      loadVideos();
    } catch (error) {
      console.error('Error reordering videos:', error);
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(videos);
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, reorderedItem);

    setVideos(reorderedItems);
    reorderVideos(reorderedItems);
  };

  useEffect(() => {
    loadVideos();
  }, [moduleId]);

  if (isLoading) {
    return <div>Loading videos...</div>;
  }

  return (
    <Row>
      <Col>
        {isEditing ? (
          <VideoForm
            initialValues={
              isEditing === 'new'
                ? getEmptyVideo()
                : videos.find((v) => {
                  console.log('Comparing:', v.id, isEditing); // Debug log
                  return v.id === isEditing;
                }) || getEmptyVideo()
            }
            onSave={handleAddEdit}
            onCancel={() => setIsEditing(null)}
          />
        ) : (
          <>
            <div className="mb-3">
              <Button onClick={() => setIsEditing('new')}>Add New Video</Button>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="videos">
                {(provided) => (
                  <ul
                    className="item-list no-list-style"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {videos
                      .sort((a, b) => a.order - b.order)
                      .map((video, index) => (
                        <Draggable
                          key={video.id}
                          draggableId={video.id}
                          index={index}
                        >
                          {(dragProvided) => (
                            <li
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className="list-item"
                            >
                              <div className="d-flex">
                                <span className="me-3 mt-2">⋮⋮</span>
                                <div className="flex-grow-1">
                                  <Row className="g-4">
                                    {/* German Video */}
                                    <Col md={4}>
                                      <div className="video-item">
                                        <h6 className="mb-3">German Version</h6>
                                        <AdminVideoPreview id={video.videoId} />
                                        {video.title && (
                                          <div className="mt-2">
                                            {video.title}
                                          </div>
                                        )}
                                      </div>
                                    </Col>

                                    {/* French Video */}
                                    <Col md={4}>
                                      <div className="video-item">
                                        <h6 className="mb-3">French Version</h6>
                                        <AdminVideoPreview id={video.videoId_fr} />
                                        {video.title_fr && (
                                          <div className="mt-2">
                                            {video.title_fr}
                                          </div>
                                        )}
                                      </div>
                                    </Col>

                                    {/* Italian Video */}
                                    <Col md={4}>
                                      <div className="video-item">
                                        <h6 className="mb-3">Italian Version</h6>
                                        <AdminVideoPreview id={video.videoId_it} />
                                        {video.title_it && (
                                          <div className="mt-2">
                                            {video.title_it}
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  </Row>

                                  <div className="list-item-actions mt-4 border-top pt-3">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => video.id && setIsEditing(video.id)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => video.id && handleDelete(video.id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </li>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </Col>
    </Row>
  );
}
